<template>
  <v-card flat class="px-4">
    <v-row no-gutters class="py-0 px-2">
      <v-col cols="12" class="px-0">
        <a type="primary" size="xsmall" @click="beforeLeave">
          <v-icon size="12" class="backIcon">mdi-arrow-left</v-icon>
          {{ $t('message.actions.return') }}
        </a>
      </v-col>
      <v-col cols="12">
        <span
          class="body-bold-medium page__smartAutoplay--title"
          style="vertical-align: middle"
        >
          {{ $t('message.pages.videoPage.settings.options.smartAutoplay') }}
        </span>
      </v-col>
    </v-row>

    <v-row no-gutters class="py-1 px-2 mb-2">
      <span class="body-regular-smaller page__smartAutoplay--title-desc">
        {{ $t('message.pages.video.messageAutoplay') }}
        <a
          @click="openHelp"
          type="secondary"
          class="body-bold-smaller page__smartAutoplay--link"
          >{{ $t('message.pages.video.learnMoreHere') }}
          <v-icon class="body-bold-medium" size="12">mdi-open-in-new</v-icon></a
        >
      </span>
      <v-col cols="12" class="mt-8">
        <v-row>
          <v-col cols="12">
            <span class="body-bold-base">{{
              $t('message.pages.video.controlsPage.settings')
            }}</span>
          </v-col>
        </v-row>
        <v-row>
          <!-- autoplay -->
          <v-col cols="12" class="d-flex justify-space-between pt-0">
            <div class="d-flex flex-column">
              <span class="body-regular-small feat-title">{{
                $t('message.pages.videoEditModal.smartAutoplay')
              }}</span>
              <span class="body-regular-smaller feat-describe">{{
                $t('message.pages.videoEditModal.smartAutoplayDesc')
              }}</span>
            </div>
            <v-switch
              v-model="autoplay"
              hide-details
              :disabled="!hasEditVideoPerm"
              @click="changePlayer({ forceReset: true })"
            />
          </v-col>

          <!-- smart autoplay -->
          <v-col cols="6" class="pt-1">
            <div class="d-flex justify-space-between">
              <div class="d-flex flex-column">
                <span class="body-regular-small feat-title">{{
                  $t('message.pages.videoEditModal.smartAutoplayFeat')
                }}</span>
                <span class="body-regular-smaller feat-describe">{{
                  $t('message.pages.videoEditModal.smartAutoplayFeatMessage')
                }}</span>
              </div>
            </div>
          </v-col>
          <v-col cols="6" class="pt-1 d-flex justify-end">
            <v-tooltip bottom :disabled="autoplay">
              <template v-slot:activator="{ on }">
                <div
                  v-on="on"
                  :class="{ notAllowed: !autoplay || !hasEditVideoPerm }"
                >
                  <v-switch
                    v-model="smartAutoplay"
                    class="mt-0"
                    hide-details
                    :disabled="!autoplay || !hasEditVideoPerm"
                    @click="changePlayer"
                  />
                </div>
              </template>
              <span>{{
                $t('message.pages.videoEditModal.smartAutoplayfeatTooltip')
              }}</span>
            </v-tooltip>
          </v-col>

          <!-- small muted indicator -->
          <v-col v-if="autoplay" cols="12" class="pt-1">
            <v-divider class="mt-4 mb-6"></v-divider>
            <v-row>
              <v-col cols="12">
                <span class="body-bold-base">{{
                  $t(
                    'message.pages.videoPage.smartAutoplay.adviceToActivateSound'
                  )
                }}</span>
              </v-col>
            </v-row>
            <v-row class="mt-4">
              <v-col
                cols="12"
                class="d-flex justify-space-between border-top border-thin pt-0"
              >
                <v-row>
                  <v-col cols="6">
                    <div class="d-flex flex-column">
                      <span class="body-regular-small feat-title">{{
                        $t('message.pages.videoPage.smartAutoplay.typeAdvice')
                      }}</span>
                      <span class="body-regular-smaller feat-describe">{{
                        $t(
                          'message.pages.videoPage.smartAutoplay.inviteInteract'
                        )
                      }}</span>
                    </div>
                  </v-col>
                  <v-col cols="6">
                    <v-select
                      v-model="mutedIndicatorSize"
                      hide-details
                      :disabled="!hasEditVideoPerm"
                      @change="changePlayer"
                      :menu-props="{ top: false, y: true }"
                      item-text="name"
                      item-value="value"
                      :items="[
                        {
                          value: 'none',
                          name: $t(
                            'message.pages.videoPage.smartAutoplay.sizesSmartAutoplay.none'
                          ),
                        },
                        {
                          value: 'minimalist',
                          name: $t(
                            'message.pages.videoPage.smartAutoplay.sizesSmartAutoplay.minimalist'
                          ),
                        },
                        {
                          value: 'small',
                          name: $t(
                            'message.pages.videoPage.smartAutoplay.sizesSmartAutoplay.small'
                          ),
                        },
                        {
                          value: 'medium',
                          name: $t(
                            'message.pages.videoPage.smartAutoplay.sizesSmartAutoplay.medium'
                          ),
                        },
                        {
                          value: 'large',
                          name: $t(
                            'message.pages.videoPage.smartAutoplay.sizesSmartAutoplay.fullscreen'
                          ),
                        },
                        {
                          value: 'custom',
                          name: $t(
                            'message.pages.videoPage.smartAutoplay.sizesSmartAutoplay.custom'
                          ),
                        },
                      ]"
                    ></v-select>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <div
              v-if="
                mutedIndicatorSize === 'small' ||
                mutedIndicatorSize === 'medium' ||
                mutedIndicatorSize === 'large'
              "
              class="mt-6"
            >
              <v-row>
                <v-col cols="12">
                  <span class="body-bold-base">{{
                    $t('message.pages.videoPage.smartAutoplay.aparency')
                  }}</span>
                </v-col>
              </v-row>

              <v-row class="mt-4">
                <v-col
                  cols="12"
                  class="d-flex justify-space-between border-top border-thin"
                >
                  <v-row>
                    <v-col cols="6">
                      <div class="d-flex flex-column">
                        <span class="body-regular-small feat-title">{{
                          $t('message.pages.videoPage.smartAutoplay.fontSize')
                        }}</span>
                      </div>
                    </v-col>
                    <v-col cols="6" class="d-flex">
                      <v-slider
                        v-model="mutedIndicatorFontSize"
                        class="mt-0"
                        color="primary"
                        :max="32"
                        :min="12"
                        hide-details
                        @change="changePlayer"
                        :disabled="!hasEditVideoPerm"
                      >
                      </v-slider>
                      <div class="ml-2">
                        <v-input
                          :disabled="!hasEditVideoPerm"
                          style="width: 65px"
                          size="small"
                          v-model="mutedIndicatorFontSize"
                          hide-details
                          @change="changePlayer"
                          :max="32"
                          :min="12"
                          v-mask="'##'"
                        >
                          <template #prepend>
                            <div
                              class="minmaxcontainer d-flex align-center body-regular-smaller"
                            >
                              <p
                                class="minmax first mb-0 position-absolute mt-4"
                                :style="
                                  mutedIndicatorFontSize < 99
                                    ? mutedIndicatorFontSize < 9
                                      ? ' right: 22px'
                                      : 'right: 16px'
                                    : 'right: 12px;'
                                "
                              >
                                px
                              </p>
                            </div>
                          </template>
                        </v-input>
                      </div>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>

              <v-row class="mt-1">
                <v-col
                  cols="12"
                  class="d-flex justify-space-between border-top border-thin"
                >
                  <v-row>
                    <v-col cols="6" class="d-flex align-center">
                      <div class="d-flex flex-column">
                        <span class="body-regular-small feat-title">{{
                          $t('message.pages.videoPage.smartAutoplay.topText')
                        }}</span>
                        <span class="body-regular-small feat-describe">{{
                          $t(
                            'message.pages.videoPage.smartAutoplay.topTextDesc'
                          )
                        }}</span>
                      </div>
                    </v-col>
                    <v-col cols="6" class="d-flex">
                      <v-input
                        :disabled="!hasEditVideoPerm"
                        v-model="mutedIndicatorTextTop"
                        :placeholder="
                          mutedIndicatorTextTop
                            ? mutedIndicatorTextTop
                            : $t(
                                'message.pages.video.mutedIndicator.mutedIndicatorTextPlaceholder'
                              )
                        "
                        :rules="[rules.maxLength]"
                        color="primary"
                        dense
                        outlined
                        counter="40"
                        @blur="changePlayer"
                      />
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>

              <v-row class="mt-1">
                <v-col
                  cols="12"
                  class="d-flex justify-space-between border-top border-thin"
                >
                  <v-row>
                    <v-col cols="6" class="d-flex align-center">
                      <div class="d-flex flex-column">
                        <span class="body-regular-small feat-title">{{
                          $t('message.pages.videoPage.smartAutoplay.bottomText')
                        }}</span>
                        <span class="body-regular-small feat-describe">{{
                          $t(
                            'message.pages.videoPage.smartAutoplay.bottomTextDesc'
                          )
                        }}</span>
                      </div>
                    </v-col>
                    <v-col cols="6" class="d-flex">
                      <v-input
                        :disabled="!hasEditVideoPerm"
                        v-model="mutedIndicatorTextBottom"
                        :placeholder="
                          mutedIndicatorTextBottom
                            ? mutedIndicatorTextBottom
                            : $t(
                                'message.pages.video.mutedIndicator.mutedIndicatorTextPlaceholder'
                              )
                        "
                        :rules="[rules.maxLength]"
                        color="primary"
                        dense
                        outlined
                        counter="40"
                        @blur="changePlayer"
                      />
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>

              <v-row class="mt-4">
                <v-col
                  cols="12"
                  class="d-flex justify-space-between border-top border-thin"
                >
                  <v-row>
                    <v-col cols="6" class="d-flex align-center">
                      <div class="d-flex flex-column">
                        <span class="body-regular-small feat-title">{{
                          $t('message.pages.videoPage.smartAutoplay.fontColor')
                        }}</span>
                      </div>
                    </v-col>
                    <v-col cols="6" class="d-flex justify-end">
                      <v-menu
                        :disabled="!hasEditVideoPerm"
                        offset-x
                        v-model="mutedFontColor"
                        top
                        nudge-bottom="105"
                        nudge-left="16"
                        :close-on-content-click="false"
                      >
                        <template v-slot:activator="{ on }">
                          <v-btn
                            :color="mutedIndicatorTextColor"
                            :style="setMutedFontColor"
                            v-on="on"
                            icon
                            class="rounded-pill border-slate"
                          >
                            <span class="describeHexa">{{
                              mutedIndicatorTextColor
                            }}</span></v-btn
                          >
                        </template>
                        <v-color-picker
                          v-model="mutedIndicatorTextColor"
                          flat
                          mode="hexa"
                          hide-mode-switch
                          @input="changePlayer()"
                        ></v-color-picker>
                      </v-menu>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>

              <v-row class="mt-5">
                <v-col
                  cols="12"
                  class="d-flex justify-space-between border-top border-thin"
                >
                  <v-row>
                    <v-col cols="6" class="d-flex align-center">
                      <div class="d-flex flex-column">
                        <span class="body-regular-small feat-title">{{
                          $t(
                            'message.pages.videoPage.smartAutoplay.backgroundColor'
                          )
                        }}</span>
                      </div>
                    </v-col>
                    <v-col cols="6" class="d-flex justify-end">
                      <v-menu
                        :disabled="!hasEditVideoPerm"
                        offset-x
                        v-model="mutedBackgroundColor"
                        top
                        nudge-bottom="105"
                        nudge-left="16"
                        :close-on-content-click="false"
                      >
                        <template v-slot:activator="{ on }">
                          <v-btn
                            :color="mutedIndicatorBackgroundColor"
                            :style="setMutedIndicatorBackgroundColor"
                            v-on="on"
                            icon
                            class="rounded-pill border-slate"
                          >
                            <span class="describeHexa">{{
                              mutedIndicatorBackgroundColor
                            }}</span></v-btn
                          >
                        </template>
                        <v-color-picker
                          v-model="mutedIndicatorBackgroundColor"
                          flat
                          mode="hexa"
                          hide-mode-switch
                          @input="changePlayer()"
                        ></v-color-picker>
                      </v-menu>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>

              <v-row class="mt-2">
                <v-col
                  cols="12"
                  class="d-flex justify-space-between border-top border-thin"
                >
                  <v-row>
                    <v-col cols="6" class="d-flex align-center">
                      <div class="d-flex flex-column">
                        <span class="body-regular-small feat-title">{{
                          $t(
                            'message.pages.videoPage.smartAutoplay.widthBorder'
                          )
                        }}</span>
                      </div>
                    </v-col>
                    <v-col cols="6" class="d-flex">
                      <v-slider
                        v-model="mutedIndicatorBorderSize"
                        class="mt-0"
                        color="primary"
                        :max="10"
                        :min="0"
                        hide-details
                        @change="changePlayer"
                        :disabled="!hasEditVideoPerm"
                      >
                      </v-slider>
                      <div class="ml-2">
                        <v-input
                          :disabled="!hasEditVideoPerm"
                          style="width: 65px"
                          size="small"
                          v-model="mutedIndicatorBorderSize"
                          hide-details
                          @change="changePlayer"
                          :max="10"
                          :min="0"
                          v-mask="'##'"
                        >
                          <template #prepend>
                            <div
                              class="minmaxcontainer d-flex align-center body-regular-smaller"
                            >
                              <p
                                class="minmax first mb-0 position-absolute mt-4"
                                :style="
                                  mutedIndicatorBorderSize < 9
                                    ? ' right: 22px'
                                    : 'right: 16px'
                                "
                              >
                                px
                              </p>
                            </div>
                          </template>
                        </v-input>
                      </div>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>

              <v-row class="mt-6 feat-title">
                <v-col cols="12">
                  <span class="body-bold-base">{{
                    $t('message.pages.videoPage.smartAutoplay.animation')
                  }}</span>
                </v-col>
              </v-row>

              <v-row class="mt-4">
                <v-col
                  cols="12"
                  class="d-flex justify-space-between border-top border-thin"
                >
                  <v-row>
                    <v-col cols="6">
                      <div class="d-flex flex-column">
                        <span class="body-regular-small feat-title">{{
                          $t(
                            'message.pages.videoPage.smartAutoplay.animationStart'
                          )
                        }}</span>
                      </div>
                    </v-col>
                    <v-col cols="6">
                      <v-select
                        v-model="mutedIndicatorAnimation"
                        @blur="changePlayer({ forceReset: true })"
                        @change="changePlayer({ forceReset: true })"
                        hide-details
                        :disabled="!hasEditVideoPerm"
                        :items="svgAnimationTypes"
                        :menu-props="{ top: false, y: true }"
                        item-text="name"
                        item-value="value"
                      ></v-select>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>

              <v-row class="mt-1">
                <v-col
                  cols="12"
                  class="d-flex justify-space-between border-top border-thin"
                >
                  <v-row>
                    <v-col cols="6">
                      <div class="d-flex flex-column">
                        <span class="body-regular-small">{{
                          $t(
                            'message.pages.videoPage.smartAutoplay.loopAnimation'
                          )
                        }}</span>
                      </div>
                    </v-col>
                    <v-col cols="6" class="d-flex justify-end">
                      <v-switch
                        :disabled="!hasEditVideoPerm"
                        v-model="mutedIndicatorLoop"
                        class="pa-0 ma-0 mt-2"
                        hide-details
                        @click="changePlayer"
                      />
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>

              <v-row class="mt-1" v-if="mutedIndicatorLoop">
                <v-col
                  cols="12"
                  class="d-flex justify-space-between border-top border-thin"
                >
                  <v-row>
                    <v-col cols="6">
                      <div class="d-flex flex-column">
                        <span class="body-regular-small">{{
                          $t(
                            'message.pages.videoPage.smartAutoplay.loopCoutdown'
                          )
                        }}</span>
                      </div>
                    </v-col>
                    <v-col cols="6" class="d-flex">
                      <v-slider
                        :disabled="!hasEditVideoPerm"
                        v-model="mutedIndicatorLoopDuration"
                        class="mt-0"
                        color="primary"
                        :max="32"
                        :min="12"
                        hide-details
                        @change="changePlayer"
                      >
                      </v-slider>
                      <div class="ml-2">
                        <v-input
                          :disabled="!hasEditVideoPerm"
                          style="width: 65px"
                          size="small"
                          v-model="mutedIndicatorLoopDuration"
                          hide-details
                          @change="changePlayer"
                          :max="10"
                          :min="30"
                          v-mask="'##'"
                        >
                          <template #prepend>
                            <div
                              class="minmaxcontainer d-flex align-center body-regular-smaller"
                            >
                              <p
                                class="minmax first mb-0 position-absolute mt-4"
                                style="right: 20px"
                              >
                                s
                              </p>
                            </div>
                          </template>
                        </v-input>
                      </div>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </div>
          </v-col>
        </v-row>
      </v-col>
      <v-scroll-x-transition mode="out-in" hide-in-live>
        <v-col
          v-if="mutedIndicatorSize === 'custom' && autoplay"
          cols="12"
          class="mt-4 mb-4"
        >
          <!-- personalized -->
          <v-row>
            <v-col cols="12">
              <div class="d-flex flex-wrap">
                <label
                  for="v-file-input"
                  class="insert__file rounded-lg d-flex justify-center align-center"
                >
                  <template v-if="!loadingButtonSave">
                    <div
                      class="insert__file--arrow-upload d-flex justify-center flex-column align-center"
                    >
                      <div class="img-upload">
                        <img
                          v-if="!$vuetify.theme.dark"
                          src="../../../assets/upload-arrow.svg"
                          alt="Upload icon"
                        />
                        <img
                          v-else
                          src="../../../assets/upload-arrow-dark.svg"
                          alt="Upload icon"
                        />
                      </div>
                      <div
                        class="d-flex flex-column justify-center align-center pt-2"
                      >
                        <span class="body-regular-base dropImage">{{
                          $t(
                            'message.pages.videoPage.smartAutoplay.dropImageHere'
                          )
                        }}</span>
                        <span class="body-regular-small">{{
                          $t(
                            'message.pages.videoPage.smartAutoplay.dropImageHereDesc'
                          )
                        }}</span>
                        <div class="mt-2">
                          <base-btn
                            type="secondary"
                            size="small"
                            for="v-file-input"
                            @click="$refs.fileUpload.$el.click($event)"
                            >{{
                              $t(
                                'message.pages.videoPage.smartAutoplay.deviceSearch'
                              )
                            }}
                          </base-btn>
                        </div>
                      </div>
                    </div>
                    <v-file-input
                      :disabled="loadingButtonSave"
                      id="v-file-input"
                      hide-input
                      accept="image/png, image/jpeg, image/jpg, image/gif"
                      ref="fileUpload"
                      @change="handleUpload"
                    />
                  </template>
                  <v-progress-circular
                    v-else
                    :size="22"
                    width="3"
                    color="primary"
                    indeterminate
                  />
                </label>

                <template v-if="konvaConfig.lib.length">
                  <v-radio-group v-model="selectedCustomId" style="width: 100%">
                    <v-hover
                      v-for="(item, index) in konvaConfig.lib"
                      v-slot="{ hover }"
                      :key="index"
                    >
                      <v-col
                        cols="12"
                        class="d-flex justify-space-between align-center pa-0 w-full"
                      >
                        <div class="d-flex align-center">
                          <v-radio
                            hide-details
                            :value="item.id"
                            class="mr-2"
                            @click.stop="handleCustomSelect(item)"
                          />
                          <v-card
                            :class="{
                              'on-hover': hover,
                              disabled: loadingButtonSave,
                            }"
                            class="konva__card w-full"
                            ref="selectedCustom"
                            @click.stop="handleCustomSelect(item)"
                          >
                            <v-img
                              :src="item.metadata.imgURL"
                              width="150"
                              height="85"
                            />
                          </v-card>
                          <span class="body-regular-small d-flex ml-4">{{
                            `img-${index + 1}`
                          }}</span>
                        </div>
                        <div>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <base-btn
                                size="small"
                                icon
                                type="danger"
                                v-bind="attrs"
                                v-on="{ ...on, ...$listeners }"
                                ><img
                                  src="../../../assets/pandaIcons/trash-autoplay.svg"
                                  alt="Trash Icon"
                                  @click.stop="
                                    handleCustomDelete({ item, open: true })
                                  "
                              /></base-btn>
                            </template>
                            <span>{{
                              $t('message.pages.videoEditModal.delete')
                            }}</span>
                          </v-tooltip>
                        </div>
                      </v-col>
                    </v-hover>
                  </v-radio-group>
                </template>
              </div>
            </v-col>
          </v-row>
        </v-col>
      </v-scroll-x-transition>
    </v-row>
    <span
      v-if="internalError"
      class="red--text caption align-text ml-2 mt-2 d-block"
    >
      <v-icon color="red" size="16" class="mt-n1"> mdi-alert-circle </v-icon>
      {{ $t('message.pages.video.errors.addAutoplay') }}
    </span>

    <portal to="actions-footer">
      <ActionModal
        :showAction1="hasEditVideoPerm"
        :showAction2="hasEditVideoPerm"
        :disableTooltip1="!withoutChanges"
        :disableTooltip2="!withoutChanges"
        :disabledAction1="loadingButtonSave || withoutChanges"
        :disabledAction2="loadingButtonSave || withoutChanges"
        :handleAction1="handleUndoChanges"
        :handleAction2="handleUpdateAutoplay"
        :loadingAction2="publishingChanges"
      />
    </portal>

    <!-- dialog -->
    <unsaved-changes-modal />
    <muted-custom-delete
      v-model="deletingMutedCustom"
      @removing-current="handleRemovingCurrentCustom"
      @close="handleCloseMutedDelete"
    />
  </v-card>
</template>

<script>
// external imports
import { mapActions, mapMutations, mapState, mapGetters } from 'vuex'
import _ from 'lodash'

// internal imports
import i18n from '@/translations/lang'
import colorHelpers from '@/utils/colorHelpers.js'

// API
import imagesAPI from '@/services/imagesApi.js'
import personalizedAPI from '@/services/personalizedApi.js'

// Components
import UnsavedChangesModal from '@/components/UnsavedChangesModal.vue'
import ActionModal from '@/components/ActionModal.vue'
export default {
  name: 'VideoPlayerAutoplay',
  components: {
    UnsavedChangesModal,
    ActionModal,
    MutedCustomDelete: () => import('./MutedCustomDelete.vue'),
  },
  props: {
    handleBack: {
      type: Function,
      required: true,
    },
    liveVideoId: {
      required: false,
    },
    hasEdit: {
      type: Array,
    },
  },
  inject: ['konva'],
  data: () => ({
    videoId: '',
    selectedCustomId: false,
    loadingButtonSave: false,
    profileId: null,
    profileConfig: {},
    initialConfig: {},
    autoplay: false,
    typeIncorret: false,
    smartAutoplay: false,
    mutedIndicatorIcon: false,
    mutedIndicatorAnimation: '',
    mutedIndicatorLoop: false,
    mutedIndicatorLoopDuration: 15,
    mutedIndicatorSize: 'small',
    mutedIndicatorBorderSize: 2,
    mutedIndicatorFontSize: 22,
    mutedIndicatorTextColor: '#FFFFFFFF',
    mutedIndicatorBackgroundColor: '#FF3FFFB3',
    mutedIndicatorTextBottom: '',
    mutedIndicatorTextTop: '',
    smallMutedIcon: undefined,
    rules: {
      required: (v) => (v && !!v) || i18n.t('message.requiredField'),
      maxLength: (v) =>
        v.length <= 30 || i18n.t('message.maximumLengthConfigs'),
    },
    opacity: {
      max: (v) => v <= 100,
      min: (v) => v >= 0,
    },
    animation: {
      max: (v) => v <= 100,
      min: (v) => v >= 5,
    },
    withoutChanges: true,
    mutedFontColor: false,
    mutedBackgroundColor: false,
    isBeta: false,
    deletingMutedCustom: {
      open: false,
      item: null,
    },
    publishingChanges: false,
  }),
  watch: {
    '$store.state.video.profile': {
      handler() {
        this.withoutChanges = this.checkChanges()
      },
      deep: true,
    },
    '$i18next.resolvedLanguage': {
      handler() {
        this.checkIfMutedTextIsDefault()
      },
    },
    mutedIndicatorTextBottom: {
      handler() {
        this.checkIfMutedTextIsDefault()
      },
    },
  },
  async mounted() {
    this.isBeta = location.search.match('beta')
    const scrollableElement = document.querySelector(
      '.page__smartAutoplay--title'
    )
    if (scrollableElement) {
      scrollableElement.scrollTo(0, 0)
    }
    this.mutedIndicatorTextTop = this.$t(
      'message.pages.videoEditModal.smartAutoplayTextTopMuted'
    )
    this.mutedIndicatorTextBottom = this.$t(
      'message.pages.videoEditModal.smartAutoplayTextBottomMuted'
    )
    this.videoId = this.liveVideoId ? this.liveVideoId : this.$route.params.id
    this.profileId = this.$store.state.video.video.config.profile
      ? this.$store.state.video.video.config?.profile
      : {}
    this.profileConfig = this.$store.state.video.profile?.vsl
      ? this.$store.state.video.profile?.vsl
      : {}
    this.autoplay = this.$store.state.video.profile.vsl.autoplay
      ? this.$store.state.video.profile.vsl.autoplay
      : false
    this.smartAutoplay =
      this.$store.state.video.profile?.vsl.smartAutoplay || false
    this.mutedIndicatorFontSize =
      this.$store.state.video.profile?.vsl.mutedIndicatorFontSize || 22
    this.mutedIndicatorIcon =
      this.$store.state.video.profile?.vsl.mutedIndicatorIcon
    this.mutedIndicatorTextBottom =
      this.$store.state.video.profile?.vsl.mutedIndicatorTextBottom
    this.mutedIndicatorTextTop =
      this.$store.state.video.profile?.vsl.mutedIndicatorTextTop
    this.mutedIndicatorAnimation =
      this.$store.state.video.profile?.vsl.mutedIndicatorAnimation
    this.konvaConfig.oldMutedValue = this.mutedIndicatorAnimation
    this.konvaConfig.id =
      this.$store.state.video.profile?.vsl.customMutedIndicatorId
    this.mutedIndicatorLoop =
      this.$store.state.video.profile?.vsl.mutedIndicatorLoop
    this.mutedIndicatorLoopDuration =
      this.$store.state.video.profile?.vsl.mutedIndicatorLoopDuration
    this.mutedIndicatorSize =
      this.$store.state.video.profile?.vsl.mutedIndicatorSize
    this.mutedIndicatorBorderSize =
      this.$store.state.video.profile?.vsl.mutedIndicatorBorderSize
    this.mutedIndicatorBackgroundColor = this.$store.state.video.profile?.vsl
      .mutedIndicatorBackgroundColor
      ? colorHelpers.rgbaToHex(
          this.$store.state.video.profile?.vsl.mutedIndicatorBackgroundColor
        )
      : this.$store.state.video.profile.colors.primaryColor + 'B3'
    this.mutedIndicatorTextColor = this.$store.state.video.profile?.vsl
      .mutedIndicatorTextColor
      ? colorHelpers.rgbaToHex(
          this.$store.state.video.profile?.vsl.mutedIndicatorTextColor
        )
      : this.$store.state.video.profile.colors.controlsColor + 'FF'
    this.smallMutedIcon =
      this.$store.state.video.profile?.vsl.smallMutedIcon ??
      (this.autoplay && !this.mutedIndicatorIcon)
    this.initialConfig = { ...this.profile.vsl }
    this.hasEdit.push(this.checkChanges)
    this.$root.$on('discardChanges', () => {
      this.handleUndoChanges()
      this.handleBack()
    })
    this.$root.$on('changeLanguage', () => {
      let text = this.$i18next.t('message.errors.uploadExcedeedMb')
      if (this.typeIncorret)
        text = this.$i18next.t('message.errors.invalidFileType')
      const closeBtn = document.querySelector(
        '.v-snack--active .v-snack__action button'
      )
      if (closeBtn) {
        this.UPDATE_GLOBAL_SNACKBAR({
          open: true,
          text,
          type: 'error',
          timeout: 10800000,
        })
      }
    })
    this.checkIfMutedTextIsDefault()
    this.setMutedCustom({})
    this.loadCustomTemplates()
  },
  beforeDestroy() {
    window.removeEventListener('message', this.checkWhenPlayerIsReady)
  },
  computed: {
    ...mapState({
      profile: (state) => state.video.profile,
      video: (state) => state.video.video,
      profile_id: (state) => state.video.profile_id,
      internalError: (state) => state.video.internalError,
    }),
    ...mapGetters('user', ['hasEditVideoPerm']),
    locale() {
      return this.$i18next.resolvedLanguage
    },
    playerSettingsArticle() {
      if (this.locale === 'en') {
        return 'https://help.pandavideo.com/en-us/article/how-does-smart-autoplay-work-ch7n49/'
      }
      if (this.locale === 'es') {
        return 'https://help.pandavideo.com/es/article/como-funciona-el-smart-autoplay-cfqyjc/'
      }
      return 'https://help.pandavideo.com/pt-br/article/como-funciona-o-smart-autoplay-veovz3/'
    },
    svgAnimationTypes() {
      return [
        {
          value: 'default',
          name: this.$t(
            'message.pages.video.mutedIndicator.animationsList.default'
          ),
        },
        {
          value: 'impact',
          name: this.$t(
            'message.pages.video.mutedIndicator.animationsList.impact'
          ),
        },
      ]
    },
    mutedIndicatorSizeTypes() {
      return [
        {
          name: i18n.t(
            'message.pages.video.mutedIndicator.mutedIndicatorSizeTypes.0'
          ),
          value: 'small',
        },
        {
          name: i18n.t(
            'message.pages.video.mutedIndicator.mutedIndicatorSizeTypes.1'
          ),
          value: 'medium',
        },
        {
          name: i18n.t(
            'message.pages.video.mutedIndicator.mutedIndicatorSizeTypes.2'
          ),
          value: 'large',
        },
      ]
    },
    setMutedFontColor() {
      return {
        backgroundColor: this.mutedIndicatorTextColor,
        borderRadius: '4px',
        transition: 'border-radius 200ms ease-in-out',
      }
    },
    setMutedIndicatorBackgroundColor() {
      return {
        backgroundColor: this.mutedIndicatorBackgroundColor,
        borderRadius: '4px',
        transition: 'border-radius 200ms ease-in-out',
      }
    },
    konvaConfig() {
      return this.konva
    },
  },
  methods: {
    ...mapActions('video', [
      'updateProfile',
      'createProfile',
      'setProfileToVideos',
    ]),
    ...mapMutations('video', [
      'UPDATE_VIDEO_PROFILE',
      'UPDATE_VIDEO_PROFILE_ID',
      'UPDATE_STATUS_ERROR',
    ]),
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    openHelp() {
      window.open(this.playerSettingsArticle, '_blank')
    },
    checkIfMutedTextIsDefault() {
      const textsMutedIndicator = [
        {
          top: 'Click here',
          bottom: 'to activate the sound',
        },
        {
          top: 'Pulse aquí',
          bottom: 'para activar el sonido',
        },
        {
          top: 'Clique aqui',
          bottom: 'para ativar som',
        },
      ]
      textsMutedIndicator.forEach((item) => {
        if (
          this.mutedIndicatorTextTop === item.top &&
          this.mutedIndicatorTextBottom === item.bottom
        ) {
          this.mutedIndicatorTextTop = this.$t(
            'message.pages.videoEditModal.smartAutoplayTextTopMuted'
          )
          this.mutedIndicatorTextBottom = this.$t(
            'message.pages.videoEditModal.smartAutoplayTextBottomMuted'
          )
          this.changePlayer()
        }
      })
    },
    setMutedCustom(data) {
      if (data.width) {
        this.$set(this.konvaConfig.img, 'width', data.width)
        this.$set(this.konvaConfig.img, 'height', data.height)
      } else {
        this.$delete(this.konvaConfig.img, 'width')
        this.$delete(this.konvaConfig.img, 'height')
      }

      this.konvaConfig.img.rotation = data.rotation
      this.konvaConfig.img.scaleX = data.scaleX
      this.konvaConfig.img.scaleY = data.scaleY
      this.konvaConfig.img.x = data.x
      this.konvaConfig.img.y = data.y

      // need to be the last, cuz parent component watch it
      this.konvaConfig.file = data.file || data.imgURL
    },
    async loadCustomTemplates() {
      this.loadingButtonSave = true

      try {
        const { data } = await personalizedAPI.getPersonalized()
        this.konvaConfig.lib = data
      } catch (err) {
        console.error(err)
      } finally {
        this.loadingButtonSave = false
      }
    },
    handleTabDefault() {
      let value = this.konvaConfig.oldMutedValue

      if (this.konvaConfig.oldMutedValue === 'custom') value = 'default'

      this.mutedIndicatorAnimation = value
      this.setMutedCustom({})
    },
    handleUpload(file) {
      if (file) {
        if (
          file.type !== 'image/gif' &&
          file.type !== 'image/jpeg' &&
          file.type !== 'image/png' &&
          file.type !== 'image/jpg'
        ) {
          this.typeIncorret = true
          const text = this.$i18next.t('message.errors.invalidFileType')
          this.UPDATE_GLOBAL_SNACKBAR({
            open: true,
            text,
            type: 'error',
            timeout: 10800000,
          })
          return
        }
        this.typeIncorret = false
        if (file.size > 5 * 1024 * 1024) {
          const text = this.$i18next.t('message.errors.uploadExcedeedMb')
          this.UPDATE_GLOBAL_SNACKBAR({
            open: true,
            text,
            type: 'error',
            timeout: 10800000,
          })
          return
        }
        this.closeSnackbar()
        this.checkPlayerToPreviewCustomMutedIndicator()
        this.konvaConfig.id = null
        this.konvaConfig.file = file
        this.profile.vsl.mutedIndicatorIcon = false
        const imgURL = URL.createObjectURL(file)
        const newItem = {
          metadata: {
            imgURL: imgURL,
          },
          file: file,
          isNew: true,
        }
        this.konvaConfig.lib.push(newItem)
      }
    },
    checkPlayerToPreviewCustomMutedIndicator() {
      window.addEventListener('message', this.checkWhenPlayerIsReady)
    },
    handlePreviewCustomMutedIndicator() {
      let functionToExecuteOnPlayer
      functionToExecuteOnPlayer = function hideMutedIndicator() {
        /* eslint-disable no-undef */
        const mutedIndicatorIframe = document.querySelector(
          '#custom_muted_indicator_iframe'
        )
        if (mutedIndicatorIframe) {
          mutedIndicatorIframe.style.display = 'none'
        }
      }
      const functionString = functionToExecuteOnPlayer.toString()
      const messageType = 'run_internal_methods'
      const message = {
        type: messageType,
        function: functionString,
        parameter: false,
      }
      const iframe = document.querySelector('iframe')
      iframe.contentWindow.postMessage(message, '*')
    },
    checkWhenPlayerIsReady(event) {
      const { data } = event
      if (
        data.message === 'panda_ready' ||
        data.message === 'panda_timeupdate'
      ) {
        this.handlePreviewCustomMutedIndicator()
        window.removeEventListener('message', this.checkWhenPlayerIsReady)
      }
    },
    async handleCustomSelect({ id, metadata }) {
      this.selectedCustomId = id
      this.konvaConfig.id = id

      this.setMutedCustom(metadata)

      // hide template from preview
      this.profile.vsl.mutedIndicatorIcon = false
      this.checkPlayerToPreviewCustomMutedIndicator()
    },
    async handleCustomDelete({ open, item }) {
      if (item.isNew) {
        const index = this.konvaConfig.lib.indexOf(item)
        if (index > -1) {
          this.konvaConfig.lib.splice(index, 1)
        }
        this.setMutedCustom({})
        this.$root.$emit('updateEmbedPreviewChange')
        this.konvaConfig.file = null
        this.loadCustomTemplates()
        return
      }
      this.deletingMutedCustom.open = open
      this.deletingMutedCustom.item = item
    },
    handleCloseMutedDelete() {
      this.loadingButtonSave = false

      this.deletingMutedCustom.open = false
      this.deletingMutedCustom.item = null

      this.loadCustomTemplates()
    },
    async handleRemovingCurrentCustom() {
      this.loadingButtonSave = false

      this.deletingMutedCustom.open = false
      this.deletingMutedCustom.item = null

      this.konvaConfig.id = null

      this.setMutedCustom({})

      this.mutedIndicatorAnimation = 'default'
      this.mutedIndicatorIcon = false

      await this.handleUpdateAutoplay()
    },
    getCustomScript(metadata, src) {
      const { video_external_id } = this.video
      const iframe = document.querySelector('#panda-player')
      const { width: width_ref, height: height_ref } =
        iframe.getBoundingClientRect()
      let script = ''
      const dStyle =
        'position:absolute;width:100%;height:100%;overflow:hidden;display: flex;'

      const { rotation, scaleX, scaleY, width, x, y, height } = metadata

      const iStyle = `position:absolute;
                      visibility:hidden;
                      width:${width}px;
                      height:${height}px;
                      transform-origin: top left;
                      transform:translate(${x}px, ${y}px) rotate(${rotation}deg) scale(${scaleX}, ${scaleY});`

      script += `<div id="template_${video_external_id}" style="${dStyle}"><img src="${src}" style="${iStyle}"></div>`

      const innerHtml = `function isIOS() {
                             return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
                         }
                         function resize_template(){
                           var template_div = document.getElementById("template_${video_external_id}"),
                               img_element = template_div.querySelector("img"),
                               muted_indicator_element = template_div.closest("html"),
                               dashboard_config = ${JSON.stringify(metadata)};

                           const width_ref = ${width_ref},
                                 height_ref = ${height_ref},
                                 player_width = muted_indicator_element.offsetWidth,
                                 player_height = muted_indicator_element.offsetHeight;

                           var ref_aspect_ratio = width_ref / height_ref;
                           var current_aspect_ratio = player_width / player_height;

                           var actual_video_width = player_width;
                           var black_bar_offset = 0;

                           if (current_aspect_ratio > ref_aspect_ratio) {
                             actual_video_width = player_height * ref_aspect_ratio;
                             black_bar_offset = (player_width - actual_video_width) / 2;
                           }

                           var relativeX = (dashboard_config.x / width_ref) * actual_video_width + black_bar_offset;
                           var relativeY = (dashboard_config.y / height_ref) * player_height;
                           var relative_scaleX = dashboard_config.scaleX * (player_width / width_ref);
                           var relative_scaleY = dashboard_config.scaleY * (player_height / height_ref);
                           var rotation = dashboard_config.rotation;

                           if (current_aspect_ratio !== ref_aspect_ratio) {
                             if (current_aspect_ratio > ref_aspect_ratio) {
                               relative_scaleX *= (ref_aspect_ratio / current_aspect_ratio);
                             } else {
                               relative_scaleY *= (current_aspect_ratio / ref_aspect_ratio);
                             }
                           }

                           if (img_element) {
                             img_element.style.position = 'absolute';
                             img_element.style.transformOrigin = 'top left';
                             img_element.style.transform = 'translate(' + relativeX + 'px, ' + relativeY + 'px) rotate(' + rotation + 'deg) scale(' + relative_scaleX + ', ' + relative_scaleY + ')';
                             img_element.style.visibility = 'visible';
                           }
                         }

                         function checkElement(){
                           document.getElementById("template_${video_external_id}"),
                           clearInterval(intervalId),
                           resize_template(),
                           window.addEventListener("resize", debounce(resize_template, 100), !1)
                         }

                         const debounce = (func, delay) => {
                           let timeout;
                           return function() {
                             clearTimeout(timeout);
                             timeout = setTimeout(func, delay);
                           };
                         }

                         const intervalId = setInterval(checkElement, 100);
                         window.onload = function(){
                           checkElement()
                         };`
      /* eslint-disable no-useless-escape */
      script += `<script>
                  var scp = document.createElement('script')
                  scp.innerHTML = ${innerHtml}
                  document.head.appendChild(scp)
                <\/script>`

      return script
    },
    getCustomOptions(src, id) {
      // eslint-disable-next-line no-unused-vars
      const { name, image, draggable, ...metadata } = this.konvaConfig.img
      const { stage } = this.konvaConfig

      // needed to ...
      metadata.imgURL = src // reload template
      metadata.imgID = id // reload template
      metadata.stage = stage // calc dimension

      // gen options
      return {
        script: this.getCustomScript(metadata, src),
        metadata,
      }
    },
    async saveImage() {
      const blob = this.konvaConfig.file
      const options = [
        {
          filename: this.konvaConfig.file.name,
          type: 'template',
        },
      ]

      return await imagesAPI.handleUploadImage(blob, options)
    },
    async saveCustom(data) {
      const src = `${data.cdn_base}/${data.storage_path}`
      const options = this.getCustomOptions(src, data.id)

      try {
        const customData = await personalizedAPI.createPersonalized(options)
        this.konvaConfig.id = customData.data.id
      } catch (err) {
        console.error(err)
      }
    },
    async updateCustom() {
      const { id } = this.konvaConfig
      const item = _.find(this.konvaConfig.lib, { id })

      const options = this.getCustomOptions(
        this.konvaConfig.file,
        item.metadata.imgID
      )

      try {
        await personalizedAPI.updatePersonalized(id, options)
      } catch (err) {
        console.error(err)
      }
    },
    async handleMutedCustom() {
      const hasID = !!this.konvaConfig.id
      const hasFile = !!this.konvaConfig.file

      if (this.mutedIndicatorAnimation === 'custom') {
        // handle create
        if (!hasID && hasFile) {
          try {
            const imgData = await this.saveImage()
            await this.saveCustom(imgData)
          } catch (err) {
            console.error(err)
          }
        }

        // handle update
        else if (hasID && hasFile) {
          await this.updateCustom()
        }

        this.setMutedCustom({})
      }
    },
    async handleUpdateAutoplay() {
      this.loadingButtonSave = true
      this.publishingChanges = true

      await this.handleMutedCustom()

      const vsl = {
        ...this.profile.vsl,
        mutedIndicatorIcon: this.mutedIndicatorIcon,
        smartAutoplay: this.smartAutoplay,
        mutedIndicatorTextTop: this.mutedIndicatorTextTop,
        mutedIndicatorTextBottom: this.mutedIndicatorTextBottom,
        autoplay: this.autoplay,
        mutedIndicatorAnimation: this.mutedIndicatorAnimation,
        customMutedIndicatorId: this.konvaConfig.id,
        mutedIndicatorLoop: this.mutedIndicatorLoop,
        mutedIndicatorFontSize: this.mutedIndicatorFontSize,
        mutedIndicatorLoopDuration: this.mutedIndicatorLoopDuration,
        mutedIndicatorBackgroundColor: colorHelpers.hexToRgba(
          this.mutedIndicatorBackgroundColor
        ),
        mutedIndicatorTextColor: colorHelpers.hexToRgba(
          this.mutedIndicatorTextColor
        ),
        mutedIndicatorSize: this.mutedIndicatorSize,
        mutedIndicatorBorderSize: this.mutedIndicatorBorderSize,
        smallMutedIcon: this.smallMutedIcon,
      }

      if (
        this.mutedIndicatorTextBottom.length > 30 ||
        this.mutedIndicatorTextTop.length > 30
      ) {
        this.loadingButtonSave = false
        this.publishingChanges = false
        return
      }

      if (!this.video.config.profile) {
        await this.createProfile(this.videoId)
        this.video.config.profile = this.profile_id
        this.profileId = this.profile_id
        await this.setProfileToVideos([this.profileId, this.videoId])
      }

      const controls = this.$store.state.video.profile.controls
      const colors = this.$store.state.video.profile.colors
      try {
        await this.updateProfile([
          this.profileId,
          {
            vsl: vsl,
            colors: colors,
            controls: controls,
          },
        ])
        this.internalError
          ? ''
          : ((this.initialConfig = _.cloneDeep(this.profile.vsl)),
            this.handleSnackbar(this.$t('message.pages.video.changesSaved')),
            (this.withoutChanges = true))
      } catch (e) {
        console.error(e)
      } finally {
        this.loadingButtonSave = false
        this.publishingChanges = false

        if (this.mutedIndicatorAnimation === 'custom') {
          this.konvaConfig.id =
            this.$store.state.video.profile?.vsl.customMutedIndicatorId
          this.konvaConfig.file = null

          await this.loadCustomTemplates()

          const iframe = document.getElementById('panda-player').contentWindow
          const item = _.find(this.konva.lib, { id: this.konva.id })
          const parameter = {
            script: item.script,
            metadata: item.metadata,
          }
          const run = () =>
            iframe.postMessage(
              { type: 'custom_muted_indicator', parameter },
              '*'
            )

          if (item) setTimeout(run, 1500)
        }
      }
      this.$root.$emit('updateEmbedPreviewChange')
      this.publishingChanges = false
    },
    changePlayer({ forceReset = false } = {}) {
      if (this.profile.vsl === null) {
        this.profile.vsl = {}
      }

      this.profile.vsl['autoplay'] = this.autoplay

      if (forceReset && !this.autoplay) {
        this.smartAutoplay = false
        this.mutedIndicatorIcon = false
      }
      if (
        (this.autoplay && this.mutedIndicatorSize === 'small') ||
        this.mutedIndicatorSize === 'medium' ||
        this.mutedIndicatorSize === 'large'
      ) {
        this.mutedIndicatorAnimation === 'custom'
          ? (this.mutedIndicatorAnimation = 'impact')
          : this.mutedIndicatorAnimation
        this.mutedIndicatorIcon = true
        this.smallMutedIcon = false
      } else if (this.autoplay && this.mutedIndicatorSize === 'none') {
        this.mutedIndicatorIcon = false
        this.smallMutedIcon = false
      } else if (this.autoplay && this.mutedIndicatorSize === 'custom') {
        this.mutedIndicatorAnimation = 'custom'
        this.setMutedCustom({})
        this.mutedIndicatorIcon = true
        this.smallMutedIcon = false
      } else if (this.autoplay) {
        this.mutedIndicatorIcon = false
        this.smallMutedIcon = true
      }

      this.profile.vsl['mutedIndicatorIcon'] = this.mutedIndicatorIcon
      this.profile.vsl['mutedIndicatorFontSize'] = this.mutedIndicatorFontSize
      this.profile.vsl['smartAutoplay'] = this.smartAutoplay
      this.profile.vsl['mutedIndicatorTextBottom'] =
        this.mutedIndicatorTextBottom
      this.profile.vsl['mutedIndicatorTextTop'] = this.mutedIndicatorTextTop
      this.profile.vsl['mutedIndicatorAnimation'] = this.mutedIndicatorAnimation
      this.profile.vsl['customMutedIndicatorId'] = this.konvaConfig.id
      this.profile.vsl['mutedIndicatorLoop'] = this.mutedIndicatorLoop
      this.profile.vsl['mutedIndicatorLoopDuration'] =
        this.mutedIndicatorLoopDuration
      this.profile.vsl['mutedIndicatorTextColor'] = colorHelpers.hexToRgba(
        this.mutedIndicatorTextColor
      )
      this.profile.vsl['mutedIndicatorSize'] = this.mutedIndicatorSize
      this.profile.vsl['mutedIndicatorBorderSize'] =
        this.mutedIndicatorBorderSize
      this.profile.vsl['mutedIndicatorBackgroundColor'] =
        colorHelpers.hexToRgba(this.mutedIndicatorBackgroundColor)
      this.profile.vsl['smallMutedIcon'] = this.smallMutedIcon

      this.UPDATE_STATUS_ERROR(false)
      this.UPDATE_VIDEO_PROFILE(this.profile)
      const iframe = document.getElementById('panda-player').contentWindow
      iframe.postMessage(
        {
          type: 'colors',
          parameter: {
            colors: {
              mutedIndicatorTextColor: colorHelpers.hexToRgba(
                this.mutedIndicatorTextColor
              ),
              mutedIndicatorBackgroundColor: colorHelpers.hexToRgba(
                this.mutedIndicatorBackgroundColor
              ),
            },
          },
        },
        '*'
      )
      this.$root.$emit('updateEmbedPreviewChange')
      if (forceReset && this.autoplay === false) {
        window.addEventListener('message', this.handleMessage)
      }
    },
    pausePlayer() {
      const iframe = document.getElementById('panda-player').contentWindow
      iframe.postMessage(
        {
          type: 'play_toggle',
        },
        '*'
      )
    },
    handleMessage(event) {
      const message = event.data.message

      if (message === 'panda_play') {
        this.pausePlayer()
      }
      if (message === 'panda_pause') {
        window.removeEventListener('message', this.handleMessage)
      }
    },
    handleUndoChanges() {
      this.autoplay = _.cloneDeep(this.initialConfig.autoplay)
      this.smartAutoplay = _.cloneDeep(this.initialConfig.smartAutoplay)
      this.mutedIndicatorIcon = _.cloneDeep(
        this.initialConfig.mutedIndicatorIcon
      )
      this.mutedIndicatorBackgroundColor = _.cloneDeep(
        colorHelpers.rgbaToHex(this.initialConfig.mutedIndicatorBackgroundColor)
      )
      this.mutedIndicatorTextColor = _.cloneDeep(
        colorHelpers.rgbaToHex(this.initialConfig.mutedIndicatorTextColor)
      )
      this.mutedIndicatorTextBottom = _.cloneDeep(
        this.initialConfig.mutedIndicatorTextBottom
      )
      this.mutedIndicatorTextTop = _.cloneDeep(
        this.initialConfig.mutedIndicatorTextTop
      )
      this.mutedIndicatorAnimation = _.cloneDeep(
        this.initialConfig.mutedIndicatorAnimation
      )
      this.mutedIndicatorLoop = _.cloneDeep(
        this.initialConfig.mutedIndicatorLoop
      )
      this.mutedIndicatorLoopDuration = _.cloneDeep(
        this.initialConfig.mutedIndicatorLoopDuration
      )
      this.mutedIndicatorSize = _.cloneDeep(
        this.initialConfig.mutedIndicatorSize
      )
      this.mutedIndicatorBorderSize = _.cloneDeep(
        this.initialConfig.mutedIndicatorBorderSize
      )
      this.mutedIndicatorFontSize = _.cloneDeep(
        this.initialConfig.mutedIndicatorFontSize
      )
      this.smallMutedIcon = _.cloneDeep(this.initialConfig.smallMutedIcon)
      this.konvaConfig.id = _.cloneDeep(
        this.initialConfig.customMutedIndicatorId
      )

      this.konvaConfig.file = null
      this.changePlayer()
      this.loadCustomTemplates()
    },
    checkChanges() {
      return _.isEqual(this.profile.vsl, this.initialConfig)
    },
    beforeLeave() {
      if (!this.checkChanges()) {
        this.$root.$emit('unsavedChangesModal')
      } else {
        this.UPDATE_STATUS_ERROR(false)
        this.closeSnackbar()
        this.handleBack()
      }
    },
    closeSnackbar() {
      const closeBtn = document.querySelector(
        '.v-snack--active .v-snack__action button'
      )
      if (closeBtn) closeBtn.click()
    },
    handleSnackbar(item) {
      if (!item) {
        item = i18n.t('message.changesSaved')
      }
      this.UPDATE_GLOBAL_SNACKBAR({
        open: true,
        text: item,
      })
    },
    numberOnly(evt) {
      const event = evt || window.event
      const expect = event.target.value.toString() + event.key.toString()

      if (!/^[0-9]*\.?[0-9]*$/.test(expect)) evt.preventDefault()
      else return true
    },
  },
}
</script>

<style lang="scss" scoped>
@mixin shadow($color, $size: 1px) {
  box-shadow: $size $size 2px $color, #{$size * -1} #{$size * -1} 2px $color !important;
}
.notAllowed {
  cursor: not-allowed;
}
.backIcon {
  margin-top: 0.1rem;
}
.v-input--radio-group--column .v-radio:not(:last-child):not(:only-child) {
  margin-bottom: 0;
}
.konva__card {
  width: 48px !important;
  height: 32px !important;
  margin: 0.5rem 0 !important;
  & .v-image {
    width: 48px !important;
    height: 32px !important;
  }
}
.page__smartAutoplay {
  &--back,
  &--back-icon {
    color: varFigma(brand-60-base);
  }

  &--title,
  .feat-title {
    color: varFigma(neutral-20-dark);
  }
  &--title-desc,
  .feat-describe {
    color: varFigma(neutral-40-light);
  }
}
.feat-title {
  color: varFigma(neutral-20-dark);
}
.feat-describe {
  color: varFigma(neutral-40-light);
}
.insert__file {
  background-color: varFigma(neutral-99);
  width: 100%;
  min-height: 263px;
  border: 2px dashed varFigma(neutral-95-lightest);
  cursor: pointer;

  .v-text-field.v-input--dense {
    display: none;
  }
  &--arrow-upload {
    width: 100%;

    .img-upload {
      padding: 1.2rem 1.5rem;
      background-color: varFigma(brand-90);
      border-radius: 50px;
    }
  }

  span.dropImage {
    color: varFigma(neutral-20-dark);
  }
  span {
    color: varFigma(neutral-40-light);
  }
}
.divider {
  &__values {
    border-color: var(--v-neutral-lighten2);
    border-style: solid;
    margin: 6px 0;
  }
}
.position-absolute {
  position: absolute;
}
.konva {
  &__card {
    margin: 0 1rem 1rem 0;

    display: flex;
    width: 150px;
    height: 85px;
    padding: 0;
    box-shadow: none !important;
    box-sizing: content-box;

    justify-content: center;
    align-items: center;

    &:not(label) {
      border: 1px solid var(--v-neutral-lighten3);
    }

    &:not([class*='--disabled']) {
      cursor: pointer;
    }

    &:hover:not([class*='--disabled']) {
      @include shadow(var(--v-neutral-lighten3));
    }

    &:active:not([class*='--disabled']) {
      @include shadow(var(--v-neutral-lighten2));
    }

    &.disabled {
      position: relative;
      pointer-events: none;
      overflow: hidden;

      &::after {
        content: '';

        position: absolute;
        top: 0;
        left: 0;
        z-index: 1;

        display: block;
        width: 100%;
        height: 100%;
        background-color: rgba(#000, 0.35);
      }
    }

    &--span {
      position: absolute;
      top: 43px;

      font-size: 12px;
      text-align: center;
      color: var(--v-neutral-lighten1);
    }

    &--input {
      display: flex;
      width: 100%;
      height: 100%;
      border-style: dashed;
      border-color: var(--v-neutral-lighten2);
      box-shadow: none !important;

      justify-content: center;
      align-items: center;
    }

    &::v-deep {
      .v-input {
        margin: 0;

        display: flex;
        width: 100%;
        height: 100%;
        padding: 0;

        justify-content: center;
        align-items: center;

        &__prepend-outer {
          margin: 0 0 1rem;
        }
        .v-icon {
          color: var(--v-neutral-lighten1);
        }
      }
    }
  }

  &__iframe {
    border: none;
    overflow: hidden;
    pointer-events: none;
  }
}
.border-slate {
  height: 16px !important;
  width: 16px !important;
  border: varFigma(neutral-30-base) 1px solid;
}
.describeHexa {
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 10%;
  color: varFigma(neutral-40-light);
  margin-right: 130px;
  width: 100px;
  display: flex;
  justify-content: end;
}
.v-textfield {
  background-color: #ffffffb3;

  &::v-deep {
    input[type='number'] {
      -webkit-appearance: none;
      -moz-appearance: textfield;
      font-size: 1.125em;
    }
  }
}
</style>
